// contentFileUpload.tsx
// ─────────────────────────────────────────────────────────────────────────────
// ContentFileUpload component
// 464×128px container, dashed border, centered icon button + text
// Only links trigger actions (NOT the container)
// ─────────────────────────────────────────────────────────────────────────────

import React, { useRef, useState, DragEvent, ChangeEvent } from 'react';
import '../css/contentFileUpload.css';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface ContentFileUploadProps {
  /** Accepted file types, e.g. ".jpg,.jpeg,.png,.svg" */
  accept?: string;

  /** Support text shown below the link row */
  supportText?: string;

  /** Called when user selects or drops files */
  onFilesSelected?: (files: File[]) => void;

  /** Called when "choose your file from media" is clicked */
  onMediaClick?: () => void;

  /** Called when "choose your file from device" is clicked */
  onDeviceClick?: () => void;

  /** Optional extra className on container */
  className?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

const ContentFileUpload: React.FC<ContentFileUploadProps> = ({
  accept = '.jpg,.jpeg,.png,.svg',
  supportText = 'JPG, PNG or SVG',
  onFilesSelected,
  onMediaClick,
  onDeviceClick,
  className = '',
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  // ── Handlers ─────────────────────────────────────────────────────────────

  const triggerInput = () => inputRef.current?.click();

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? /* istanbul ignore next */ []);
    if (files.length) onFilesSelected?.(files);
    e.target.value = '';
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length) onFilesSelected?.(files);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleDeviceClick = () => {
    if (onDeviceClick) {
      onDeviceClick();
    } else {
      triggerInput(); // fallback to native file picker
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div //NOSONAR
      className={`scl--cms-components-contentFileUpload-container${
        isDragging ? ' scl--cms-components-contentFileUpload-container--dragging' : ''
      }${className ? ` ${className}` : ''}`} //NOSONAR
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
    >
      {/* Hidden native file input */}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple
        className="scl--cms-components-contentFileUpload-input"
        onChange={handleChange}
      />

      {/* Icon button */}
      <div
        className="scl--cms-components-contentFileUpload-icon-btn"
        aria-hidden="true"
      >
        <svg
          className="scl--cms-components-contentFileUpload-icon"
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M10 13V4M10 4L6.5 7.5M10 4L13.5 7.5"
            stroke="#323539"
            strokeWidth="1.67"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M3 14.5V16C3 16.55 3.45 17 4 17H16C16.55 17 17 16.55 17 16V14.5"
            stroke="#323539"
            strokeWidth="1.67"
            strokeLinecap="round"
          />
        </svg>
      </div>

      {/* Text area */}
      <div className="scl--cms-components-contentFileUpload-text">
        <div className="scl--cms-components-contentFileUpload-header">

          <span //NOSONAR
            className="scl--cms-components-contentFileUpload-link"
            onClick={onMediaClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) =>
              (e.key === 'Enter' || e.key === ' ') && onMediaClick?.()
            }
          >
            choose your file
          </span>
          <span className="scl--cms-components-contentFileUpload-plain">
            &nbsp;form media&nbsp;
          </span>
          <span //NOSONAR
            className="scl--cms-components-contentFileUpload-link"
            onClick={handleDeviceClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) =>
              (e.key === 'Enter' || e.key === ' ') && handleDeviceClick()
            }
          >
            choose your file
          </span>

          <span className="scl--cms-components-contentFileUpload-plain">
            &nbsp;form device
          </span>

        </div>

        <div className="scl--cms-components-contentFileUpload-support">
          {supportText}
        </div>
      </div>
    </div>
  );
};

export default ContentFileUpload;
